import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { Warehouse, Plus, Edit2, Trash2, X, Building2, Package } from 'lucide-react';

export default function BodegasPage() {
    const [bodegas, setBodegas] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', sucursal_id: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const authHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [bodegasRes, sucursalesRes] = await Promise.all([
                fetch(`${API_URL}/api/bodegas/stats`, { headers: authHeaders() }),
                fetch(`${API_URL}/api/sucursales`, { headers: authHeaders() })
            ]);
            
            const bodegasData = await bodegasRes.json();
            const sucursalesData = await sucursalesRes.json();
            
            setBodegas(bodegasData);
            setSucursales(sucursalesData);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (bodega = null) => {
        if (bodega) {
            setEditingId(bodega.id);
            setFormData({
                nombre: bodega.nombre || '',
                sucursal_id: bodega.sucursal_id?.toString() || ''
            });
        } else {
            setEditingId(null);
            setFormData({ nombre: '', sucursal_id: sucursales[0]?.id?.toString() || '' });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.sucursal_id) {
            alert('Debe seleccionar una sucursal');
            return;
        }
        
        try {
            const url = editingId 
                ? `${API_URL}/api/bodegas/${editingId}`
                : `${API_URL}/api/bodegas`;
            
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    nombre: formData.nombre,
                    sucursal_id: parseInt(formData.sucursal_id)
                })
            });

            if (res.ok) {
                setShowModal(false);
                fetchData();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al guardar');
            }
        } catch (err) {
            console.error('Error saving bodega:', err);
            alert('Error al guardar la bodega');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta bodega?')) return;
        
        try {
            const res = await fetch(`${API_URL}/api/bodegas/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) {
                fetchData();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al eliminar');
            }
        } catch (err) {
            console.error('Error deleting bodega:', err);
            alert('Error al eliminar la bodega');
        }
    };

    // Group bodegas by sucursal
    const bodegasBySucursal = bodegas.reduce((acc, bodega) => {
        const sucursal = bodega.sucursal_nombre || 'Sin Sucursal';
        if (!acc[sucursal]) {
            acc[sucursal] = [];
        }
        acc[sucursal].push(bodega);
        return acc;
    }, {});

    return (
        <div className="page bodegas-page p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Warehouse className="text-orange-600" size={28} />
                        Gestión de Bodegas
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Administra las bodegas de cada sucursal
                    </p>
                </div>
                <button 
                    className="btn-primary flex items-center gap-2"
                    onClick={() => openModal()}
                    disabled={sucursales.length === 0}
                >
                    <Plus size={18} /> Nueva Bodega
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <Warehouse className="text-orange-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Bodegas</p>
                            <p className="text-2xl font-bold">{bodegas.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <Package className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Productos en Bodegas</p>
                            <p className="text-2xl font-bold">
                                {bodegas.reduce((sum, b) => sum + (parseInt(b.productos_count) || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <Building2 className="text-green-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Stock Total</p>
                            <p className="text-2xl font-bold">
                                {bodegas.reduce((sum, b) => sum + (parseInt(b.stock_total) || 0), 0).toLocaleString('es-CL')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
                    Cargando...
                </div>
            ) : sucursales.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                    <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Primero debes crear una sucursal</p>
                    <a href="/sucursales" className="mt-4 btn-primary inline-block">
                        Ir a Sucursales
                    </a>
                </div>
            ) : bodegas.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                    <Warehouse size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No hay bodegas creadas</p>
                    <button 
                        onClick={() => openModal()}
                        className="mt-4 btn-primary"
                    >
                        Crear primera bodega
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(bodegasBySucursal).map(([sucursal, bodegasList]) => (
                        <div key={sucursal} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-2">
                                <Building2 size={20} className="text-indigo-600" />
                                <h3 className="font-semibold text-gray-800">{sucursal}</h3>
                                <span className="text-sm text-gray-500">({bodegasList.length} bodegas)</span>
                            </div>
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Productos</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock Total</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {bodegasList.map(bodega => (
                                        <tr key={bodega.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-500">#{bodega.id}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <Warehouse size={18} className="text-orange-500" />
                                                    <span className="font-medium">{bodega.nombre}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                                    {bodega.productos_count || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                                    {(bodega.stock_total || 0).toLocaleString('es-CL')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                        onClick={() => openModal(bodega)}
                                                        title="Editar"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                        onClick={() => handleDelete(bodega.id)}
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingId ? 'Editar Bodega' : 'Nueva Bodega'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Sucursal *</label>
                                <select
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.sucursal_id}
                                    onChange={e => setFormData({ ...formData, sucursal_id: e.target.value })}
                                >
                                    <option value="">Seleccionar sucursal...</option>
                                    {sucursales.map(suc => (
                                        <option key={suc.id} value={suc.id}>{suc.nombre}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Nombre *</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej. Bodega Principal"
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                                >
                                    {editingId ? 'Guardar Cambios' : 'Crear Bodega'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
